import { onlineManager, useQuery, type QueryKey } from '@tanstack/react-query';
import { useMemo, useSyncExternalStore } from 'react';
import type { Me } from '@repo/shared';
import { api, isApiError } from './api';

/**
 * Last-fetched data of the student app, kept in localStorage so the PWA opens offline (Phase 6).
 * Scoped by user id and wiped at login / logout: never shared between accounts on one device.
 */
const PREFIX = 'sol-offline:';

// TanStack Query assumes "online" until an `offline` event fires; an installed PWA can launch offline.
if (typeof navigator !== 'undefined') onlineManager.setOnline(navigator.onLine);

interface Saved<T> {
  at: number;
  data: T;
}

export function saveOffline(key: string, data: unknown): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ at: Date.now(), data }));
  } catch {
    // Quota exceeded or storage disabled: the app still works online.
  }
}

export function loadOffline<T>(key: string): Saved<T> | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw ? (JSON.parse(raw) as Saved<T>) : null;
  } catch {
    return null;
  }
}

export function clearOffline(): void {
  try {
    for (const k of Object.keys(localStorage)) if (k.startsWith(PREFIX)) localStorage.removeItem(k);
  } catch {
    // Storage disabled.
  }
}

/** Students' session survives offline reloads: `/me` is replayed from the last successful call. */
export const OFFLINE_ME_KEY = 'me';
export function rememberMe(me: Me): void {
  if (me.roles.includes('ETUDIANT')) saveOffline(OFFLINE_ME_KEY, me);
}

export function useOnline(): boolean {
  return useSyncExternalStore(
    (cb) => onlineManager.subscribe(cb),
    () => onlineManager.isOnline(),
  );
}

/**
 * GET query seeded from (and saved to) the offline store. While offline the query is paused and keeps
 * the saved copy; `savedAt` tells the UI how old it is.
 */
export function useOfflineQuery<T>(userId: string, queryKey: QueryKey, path: string, enabled = true) {
  const storeKey = `${userId}:${JSON.stringify(queryKey)}`;
  const saved = useMemo(() => loadOffline<T>(storeKey), [storeKey]);
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const data = await api<T>(path);
      saveOffline(storeKey, data);
      return data;
    },
    enabled,
    // No retry storm on a dead network: the saved copy is shown at once.
    retry: (count, error) => isApiError(error) && error.status >= 500 && count < 2,
    initialData: saved?.data,
    // Saved copies are always refetched when the network is there.
    initialDataUpdatedAt: saved ? 0 : undefined,
  });
  // navigator.onLine is not reliable (captive portals, some WebViews): a network failure means offline too.
  const unreachable = query.isError && !isApiError(query.error);
  return { ...query, savedAt: saved?.at ?? null, unreachable };
}
